import { useEffect, useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import { usePageView } from '../../hooks/usePageView';
import newsDefaultImg from './news.jpg';
import './News.css';

const PAGE_SIZE = 6;

function News() {
  usePageView('news');
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const { t } = useLanguage();
  const n = t.news;
  const navigate = useNavigate();

  const fetchNews = useCallback(async (keyword) => {
    setLoading(true);
    try {
      const qs = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
      const res = await fetch(`/api/news${qs}`);
      const data = await res.json();
      setNewsData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews('');
  }, [fetchNews]);

  const totalPages = Math.ceil(newsData.length / PAGE_SIZE);
  const pageData = useMemo(
    () => newsData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [newsData, page],
  );

  function handlePageChange(p) {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleSearch() {
    setPage(1);
    fetchNews(search.trim());
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSearch();
  }

  return (
    <div className="news-page">
      <Helmet>
        <title>新闻动态 - 首嘉/深圳瀚阳</title>
        <meta
          name="description"
          content="首嘉/深圳瀚阳最新行业资讯、公司新闻与物流动态，了解物流行业前沿信息。"
        />
        <meta property="og:title" content="新闻动态 - 首嘉/深圳瀚阳" />
        <meta property="og:description" content="首嘉/深圳瀚阳最新行业资讯、公司新闻与物流动态，了解物流行业前沿信息。" />
        <meta property="og:url" content="https://www.sz-showjoy.com/news" />
        <meta name="keywords" content="物流新闻,行业资讯,物流动态,货运资讯,首嘉/深圳瀚阳新闻" />
        <link rel="canonical" href="https://www.sz-showjoy.com/news" />
      </Helmet>
      <div className="page-header">
        <div className="container">
          <h1>{n.pageTitle}</h1>
          <p>{n.pageSubtitle}</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="news-search">
            <input
              type="text"
              className="news-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={n.searchPlaceholder || '搜索新闻标题...'}
            />
            <button className="news-search-btn" onClick={handleSearch}>
              {n.searchBtn || '搜索'}
            </button>
          </div>

          <div className="news-grid">
            {loading ? (
              <p style={{ color: '#6b7280' }}>{n.loading}</p>
            ) : newsData.length === 0 ? (
              <p style={{ color: '#6b7280' }}>{n.noNews}</p>
            ) : (
              pageData.map((news) => (
                <article
                  key={news.uuid}
                  className={`news-card${news.is_pinned ? ' pinned' : ''}`}
                  onClick={() => navigate(`/news/${news.uuid}`)}
                >
                  <div className="news-image">
                    {news.cover_image ? (
                      <img
                        src={news.cover_image}
                        alt={news.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <img
                        src={newsDefaultImg}
                        alt={news.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                    <div className="news-category">{n.categories?.[news.category] || news.category}</div>
                    {news.is_pinned && (
                      <div className="news-pin-badge">{n.pinned || '置顶'}</div>
                    )}
                  </div>
                  <div className="news-content">
                    <div className="news-date">
                      {new Date(news.created_at).toLocaleDateString('zh-CN')}
                    </div>
                    <h3>{news.title}</h3>
                    <p>{news.summary}</p>
                    <button
                      className="read-more"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/news/${news.uuid}`);
                      }}
                    >
                      {n.readMore}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  className={`page-btn${p === page ? ' active' : ''}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                ›
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default News;
